import React, { useCallback, useState } from "react";
import { useMemo } from "react";
import {
  Body2,
  Button,
  Caption1,
  ConfirmDialog,
  Dialog,
  InfoIcon,
  Input,
  SelectTrigger,
  Tooltip,
} from "@activecollab/components";
import { t } from "i18next";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { formatDateSmart } from "~/utils/date";
import { preventNegativeAndEKeyPress } from "~/utils/utils";
import useAcSelectDate from "../Hooks/useAcSelectDate";
import SelectDateNonWorkingDays from "../SelectDate/SelectDateNonWorkingDays";

export const AddHourlyRateDialog = ({ allUserHourlyRates, defaultCurrencyCode, open, onAddHourlyRate, onCancel }) => {
  const [hourlyRate, setHourlyRate] = useState(0);
  const { dates, handleDateChange, formatDates } = useAcSelectDate();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const matchingDate = useMemo(() => {
    const dateToCompare = moment.unix(dates.from);
    return allUserHourlyRates.find((obj) => {
      const dateFromArray = moment.unix(obj.valid_from);
      return dateToCompare.isSame(dateFromArray, "day");
    });
  }, [allUserHourlyRates, dates.from]);

  const formatedDate = useMemo(
    () => formatDateSmart(matchingDate?.valid_from, "D MMM YYYY"),
    [matchingDate?.valid_from]
  );

  const confirmDialogContent = useMemo(
    () => (
      <Body2 color="secondary">
        {t(`The Internal hourly rate is already set for `)}
        <b>{t(`${formatedDate}`)}</b>
        {t(
          ` to ${matchingDate?.rate} ${defaultCurrencyCode}/h. If you overwrite this rate, all records using it will start using new rate. This may affect the Profitability report.`
        )}
      </Body2>
    ),
    [defaultCurrencyCode, formatedDate, matchingDate?.rate]
  );

  const handleRateChange = useCallback((e) => {
    setHourlyRate(e.target.value);
  }, []);

  const handleKeyDownOnInput = useCallback((e) => {
    preventNegativeAndEKeyPress(e);
  }, []);

  const addHourlyRate = useCallback(() => {
    onAddHourlyRate({
      rate: hourlyRate,
      valid_from: formatDates.from,
    });
  }, [formatDates.from, hourlyRate, onAddHourlyRate]);

  const handleSave = useCallback(() => {
    if (matchingDate) {
      return setOpenConfirmDialog(true);
    }
    if (onAddHourlyRate) {
      return addHourlyRate();
    }
  }, [addHourlyRate, matchingDate, onAddHourlyRate]);

  const handleConfirm = useCallback(() => {
    setOpenConfirmDialog(false);
    addHourlyRate();
  }, [addHourlyRate]);

  const handleCancelConfirm = useCallback(() => {
    setOpenConfirmDialog(false);
  }, []);

  const handleCancelAddHourlyRateDialog = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
      >
        <Dialog.Title>{t("Add Hourly Rate")}</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <div className="tw-flex tw-flex-col">
            <div className="tw-flex tw-gap-32">
              <div>
                <Caption1 weight="bold">{t("Internal Hourly Rate")}</Caption1>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Input
                    min={0}
                    className="tw-w-[70px] input-without-arrows"
                    onChange={handleRateChange}
                    onKeyDown={handleKeyDownOnInput}
                    value={hourlyRate}
                    type="number"
                  />
                  <Body2 color="tertiary">{defaultCurrencyCode}</Body2>
                  <Tooltip title={t("This is the default currency set in System Settings")}>
                    <InfoIcon />
                  </Tooltip>
                </div>
              </div>
              <div>
                <Caption1 weight="bold">{t("Valid from")}</Caption1>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <SelectDateNonWorkingDays
                    trigger={(text) => (
                      <SelectTrigger
                        endAdornment={<div className="tw-w-2" />}
                        className="tw-w-fit"
                      >
                        {text}
                      </SelectTrigger>
                    )}
                    required
                    selectedDays={dates}
                    changeMode="instant"
                    selectionMode="daily"
                    onSave={handleDateChange}
                    weekendIsSelectable
                    type="selectable_global_day_off"
                  />
                </div>
              </div>
            </div>
            <Caption1
              letterSpacing="regular"
              color="tertiary"
              className="tw-mt-2"
            >
              {t("New internal hourly rate will be applied to all the time records starting from the defined date")}.
            </Caption1>
          </div>
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button
            onClick={handleSave}
            className="tw-mr-3"
          >
            {t("Save")}
          </Button>
          <Button
            type="button"
            onClick={handleCancelAddHourlyRateDialog}
            variant="secondary"
          >
            {t("Cancel")}
          </Button>
        </Dialog.Actions>
      </Dialog>
      {openConfirmDialog ? (
        <ConfirmDialog
          open={openConfirmDialog}
          dialogTitle={t("Overwrite Hourly Rate")}
          dialogContent={confirmDialogContent}
          onCancel={handleCancelConfirm}
          onConfirm={handleConfirm}
          confirmBtnText={t("Overwrite")}
        />
      ) : null}
    </>
  );
};

AddHourlyRateDialog.propTypes = {
  allUserHourlyRates: PropTypes.array,
  defaultCurrencyCode: PropTypes.string,
  open: PropTypes.bool,
  onAddHourlyRate: PropTypes.func,
  onCancel: PropTypes.func,
};
